import { useState, useCallback, useRef } from 'react';
import { routes } from '../../assets/constants';
import { ApiMethod } from '../models/api-types.type';
import { useAuthContext } from './use-auth-context.hook';
import { ProductBatch, getFilesForBatch, ChunkableProduct } from '../utils/product-chunker';
import { BatchSessionManager, BatchSession, BatchUploadProgress } from '../services/batch-session.service';
import { logger } from '../../utils/logger';

// Constants
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 5000;
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 300; // 10 minutes max per batch

/**
 * Prepared file for upload
 */
interface PreparedFile {
  file: File;
  metaType: 'plank' | 'thumbnail' | 'original' | 'product_image' | 'model3d';
  productId: string;
}

/**
 * Options for starting a batch upload
 */
/**
 * Prepared product data for API submission (V2 operations format)
 */
export interface PreparedProductData {
  operations: any;  // BulkOperationsV2Dto-shaped object ({ productGroupId, familyId, familySlug, operations })
  files: Array<{ file: File; metaType: 'plank' | 'thumbnail' | 'original' | 'product_image' | 'model3d'; productId: string }>;
}

/**
 * Function to prepare a batch of products into API-ready format
 */
export type ProductBuilderFn = (products: ChunkableProduct[]) => Promise<PreparedProductData>;

export interface ChunkedUploadOptions {
  onBatchStart?: (batchIndex: number, totalBatches: number) => void;
  onBatchComplete?: (batchIndex: number, batchId: string) => void;
  onBatchFailed?: (batchIndex: number, error: string) => void;
  onProgress?: (progress: BatchUploadProgress) => void;
  onPollingUpdate?: (batchIndex: number, status: any) => void;
  productBuilder?: ProductBuilderFn;  // Function to convert EditProducts to API format
}

/**
 * Result of the chunked upload operation
 */
export interface ChunkedUploadResult {
  success: boolean;
  sessionId: string;
  totalBatches: number;
  completedBatches: number;
  failedBatches: number;
  errors: Array<{ batchIndex: number; error: string }>;
}

/**
 * Hook for managing chunked batch uploads with resume capability
 */
export const useChunkedBatchUpload = () => {
  const { sendAuthGuardedRequest } = useAuthContext();
  const [progress, setProgress] = useState<BatchUploadProgress>({
    sessionId: '',
    totalBatches: 0,
    completedBatches: 0,
    failedBatches: 0,
    currentBatchIndex: null,
    totalProducts: 0,
    completedProducts: 0,
    status: 'idle',
    estimatedRemainingBatches: 0
  });

  const isPausedRef = useRef(false);
  const currentSessionRef = useRef<string | null>(null);

  /**
   * Updates progress state and calls callback
   */
  const updateProgress = useCallback((
    updates: Partial<BatchUploadProgress>,
    onProgress?: (progress: BatchUploadProgress) => void
  ) => {
    setProgress(prev => {
      const newProgress = { ...prev, ...updates };
      if (onProgress) {
        onProgress(newProgress);
      }
      return newProgress;
    });
  }, []);

  /**
   * Polls for batch completion
   */
  const pollBatchStatus = useCallback(async (
    batchId: string,
    batchIndex: number,
    onPollingUpdate?: (batchIndex: number, status: any) => void
  ): Promise<any> => {
    logger.debug(`[ChunkedUpload] Polling batch ${batchIndex}, batchId: ${batchId}`);

    let attempts = 0;

    while (attempts < MAX_POLL_ATTEMPTS) {
      if (isPausedRef.current) {
        logger.log('[ChunkedUpload] Paused during polling, stopping');
        throw new Error('Upload paused');
      }

      try {
        const statusResponse: any = await sendAuthGuardedRequest(
          ApiMethod.GET,
          routes.products.bulkStatus(batchId)
        );

        logger.debug(`[ChunkedUpload] Batch ${batchIndex} status:`, statusResponse.summary);

        if (onPollingUpdate) {
          onPollingUpdate(batchIndex, statusResponse);
        }

        // Check if batch is complete
        const inProgress = statusResponse.summary.pending +
          (statusResponse.summary.generating_seamless || 0) +
          statusResponse.summary.processing;

        if (inProgress === 0) {
          logger.log(`[ChunkedUpload] Batch ${batchIndex} completed`);
          return statusResponse;
        }

        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
        attempts++;
      } catch (error) {
        logger.error(`[ChunkedUpload] Error polling batch ${batchIndex}:`, error);
        throw error;
      }
    }

    throw new Error(`Batch ${batchIndex} timed out after ${MAX_POLL_ATTEMPTS * POLL_INTERVAL_MS / 1000} seconds`);
  }, [sendAuthGuardedRequest]);

  /**
   * Prepares files for a specific batch from the products
   */
  const prepareFilesForBatch = useCallback((batch: ProductBatch): PreparedFile[] => {
    const files: PreparedFile[] = [];
    const seenFiles = new Set<string>();

    for (const product of batch.products) {
      const productId = (product as any).uniqueId;

      // Get plank images
      if (product.uploadedPlankImages) {
        for (const plankGroupId of batch.plankGroupIds) {
          const plankFiles = product.uploadedPlankImages[plankGroupId];
          if (plankFiles) {
            for (const file of plankFiles) {
              if (file && !seenFiles.has(file.name)) {
                files.push({
                  file,
                  metaType: 'plank',
                  productId
                });
                seenFiles.add(file.name);
              }
            }
          }
        }
      }
    }

    logger.log(`[ChunkedUpload] Prepared ${files.length} files for batch ${batch.index}`);
    return files;
  }, []);

  /**
   * Uploads a single batch
   */
  const uploadBatch = useCallback(async (
    sessionId: string,
    batch: ProductBatch,
    batchIndex: number,
    totalBatches: number,
    options: ChunkedUploadOptions = {}
  ): Promise<{ success: boolean; batchId?: string; error?: string }> => {
    const { onBatchStart, onBatchComplete, onBatchFailed, onPollingUpdate, productBuilder } = options;

    logger.log(`[ChunkedUpload] Uploading batch ${batchIndex + 1}/${totalBatches}`);

    if (onBatchStart) {
      onBatchStart(batchIndex, totalBatches);
    }

    // Prepare FormData
    const formData = new FormData();

    let operationsData: any;
    let files: PreparedFile[];

    // Use productBuilder if provided, otherwise fall back to basic conversion
    if (productBuilder) {
      logger.log(`[ChunkedUpload] Using productBuilder for batch ${batchIndex}`);
      const prepared = await productBuilder(batch.products);
      operationsData = prepared.operations;
      files = prepared.files;
    } else {
      // Fallback: try toDifference or use raw object (may not work for EditProduct)
      const productData = batch.products.map(product => {
        if (typeof (product as any).toDifference === 'function') {
          return (product as any).toDifference();
        }
        return product;
      });
      operationsData = {
        operations: productData.map((p: any) => ({ operation: 'create', data: p })),
        productGroupId: batch.productIds[0] || '',
      };
      files = prepareFilesForBatch(batch);
    }

    // Append the V2 operations payload directly
    formData.append('data', JSON.stringify(operationsData));
    formData.append('source', 'import');
    formData.append('sessionId', sessionId);
    formData.append('batchIndex', batchIndex.toString());

    // Append files — file.name is pre-set by buildAPIPayload with the correct convention
    for (const { file, metaType } of files) {
      if (metaType === 'plank' || metaType === 'product_image' || metaType === 'model3d' || metaType === 'thumbnail') {
        // Files are already named correctly by buildAPIPayload:
        // plank: material_{plankGroupId}_{index}.ext
        // product_image: image_{uuid}.ext
        // thumbnail: thumbnail_{uuid}.ext
        // model3d: material_{uuid}.ext
        formData.append('images', file, file.name);
      } else if (metaType === 'original') {
        formData.append('images', file, file.name);
      }
    }

    // Retry logic
    let lastError: string = '';
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        logger.log(`[ChunkedUpload] Batch ${batchIndex} attempt ${attempt}/${MAX_RETRIES}`);

        // Submit batch
        const response: any = await sendAuthGuardedRequest(
          ApiMethod.POST,
          routes.products.bulkUpdate,
          formData,
          {
            //@ts-ignore
            maxBodyLength: Infinity,
            timeout: 2 * 60 * 1000, // 2 minutes timeout
          }
        );

        if (!response.batchId) {
          throw new Error('Backend did not return batchId');
        }

        const batchId = response.batchId;
        logger.log(`[ChunkedUpload] Batch ${batchIndex} submitted, batchId: ${batchId}`);

        // Poll for completion
        updateProgress({ status: 'polling' }, options.onProgress);
        await pollBatchStatus(batchId, batchIndex, onPollingUpdate);

        // Mark batch as completed in session
        await BatchSessionManager.markBatchCompleted(
          sessionId,
          batchIndex,
          batchId,
          batch.products.length
        );

        if (onBatchComplete) {
          onBatchComplete(batchIndex, batchId);
        }

        return { success: true, batchId };
      } catch (error: any) {
        lastError = error?.message || 'Unknown error';
        logger.error(`[ChunkedUpload] Batch ${batchIndex} attempt ${attempt} failed:`, error);

        if (attempt < MAX_RETRIES && !isPausedRef.current) {
          logger.log(`[ChunkedUpload] Retrying batch ${batchIndex} in ${RETRY_DELAY_MS}ms`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
        }
      }
    }

    // All retries exhausted
    logger.error(`[ChunkedUpload] Batch ${batchIndex} failed after ${MAX_RETRIES} attempts`);

    await BatchSessionManager.markBatchFailed(sessionId, batchIndex, lastError);

    if (onBatchFailed) {
      onBatchFailed(batchIndex, lastError);
    }

    return { success: false, error: lastError };
  }, [sendAuthGuardedRequest, prepareFilesForBatch, pollBatchStatus, updateProgress]);

  /**
   * Starts a new chunked batch upload
   */
  const startBatchUpload = useCallback(async (
    batches: ProductBatch[],
    options: ChunkedUploadOptions = {}
  ): Promise<ChunkedUploadResult> => {
    isPausedRef.current = false;

    logger.log(`[ChunkedUpload] Starting batch upload with ${batches.length} batches`);

    // Create session
    const session = await BatchSessionManager.createSession(batches);
    currentSessionRef.current = session.sessionId;

    const totalProducts = batches.reduce((sum, b) => sum + b.products.length, 0);

    updateProgress({
      sessionId: session.sessionId,
      totalBatches: batches.length,
      completedBatches: 0,
      failedBatches: 0,
      currentBatchIndex: 0,
      totalProducts,
      completedProducts: 0,
      status: 'uploading',
      estimatedRemainingBatches: batches.length
    }, options.onProgress);

    const errors: Array<{ batchIndex: number; error: string }> = [];
    let completedCount = 0;
    let completedProducts = 0;

    // Process batches sequentially
    for (let i = 0; i < batches.length; i++) {
      if (isPausedRef.current) {
        logger.log('[ChunkedUpload] Upload paused');
        await BatchSessionManager.pauseSession(session.sessionId);
        updateProgress({ status: 'paused' }, options.onProgress);
        break;
      }

      const batch = batches[i];

      updateProgress({
        currentBatchIndex: i,
        status: 'uploading',
        estimatedRemainingBatches: batches.length - i
      }, options.onProgress);

      const result = await uploadBatch(
        session.sessionId,
        batch,
        i,
        batches.length,
        options
      );

      if (result.success) {
        completedCount++;
        completedProducts += batch.products.length;
        updateProgress({
          completedBatches: completedCount,
          completedProducts
        }, options.onProgress);
      } else {
        errors.push({ batchIndex: i, error: result.error || 'Unknown error' });
        updateProgress({
          failedBatches: errors.length
        }, options.onProgress);
      }

      // Release memory - help garbage collector
      batch.products = [];
    }

    // Determine final status
    const finalStatus = isPausedRef.current
      ? 'paused'
      : errors.length === 0
        ? 'completed'
        : completedCount > 0
          ? 'error' // partial failure
          : 'error';

    updateProgress({
      status: finalStatus,
      currentBatchIndex: null
    }, options.onProgress);

    // Update session status
    if (!isPausedRef.current) {
      const finalSession = await BatchSessionManager.getSession(session.sessionId);
      if (finalSession && finalSession.pendingBatchIndices.length === 0 && finalSession.failedBatches.length === 0) {
        // All done successfully - clear session
        await BatchSessionManager.clearSession(session.sessionId);
      }
    }

    return {
      success: errors.length === 0,
      sessionId: session.sessionId,
      totalBatches: batches.length,
      completedBatches: completedCount,
      failedBatches: errors.length,
      errors
    };
  }, [uploadBatch, updateProgress]);

  /**
   * Resumes an interrupted batch upload
   */
  const resumeBatchUpload = useCallback(async (
    sessionId: string,
    batches: ProductBatch[],
    options: ChunkedUploadOptions = {}
  ): Promise<ChunkedUploadResult> => {
    isPausedRef.current = false;

    logger.log(`[ChunkedUpload] Resuming session ${sessionId}`);

    const session = await BatchSessionManager.getSession(sessionId);
    if (!session) {
      throw new Error(`Session ${sessionId} not found`);
    }

    currentSessionRef.current = sessionId;

    await BatchSessionManager.resumeSession(sessionId);

    // Get pending batch indices
    const pendingIndices = session.pendingBatchIndices;
    const completedCount = session.completedBatches.length;

    logger.log(`[ChunkedUpload] Resuming with ${pendingIndices.length} pending batches`);

    const totalProducts = session.totalProducts;
    const completedProducts = session.completedBatches.reduce((sum, b) => sum + b.productCount, 0);

    updateProgress({
      sessionId,
      totalBatches: session.totalBatches,
      completedBatches: completedCount,
      failedBatches: session.failedBatches.length,
      currentBatchIndex: pendingIndices[0] ?? null,
      totalProducts,
      completedProducts,
      status: 'uploading',
      estimatedRemainingBatches: pendingIndices.length
    }, options.onProgress);

    const errors: Array<{ batchIndex: number; error: string }> = [];
    let newCompletedCount = completedCount;
    let currentCompletedProducts = completedProducts;

    // Process remaining batches
    for (const batchIndex of pendingIndices) {
      if (isPausedRef.current) {
        logger.log('[ChunkedUpload] Upload paused');
        await BatchSessionManager.pauseSession(sessionId);
        updateProgress({ status: 'paused' }, options.onProgress);
        break;
      }

      const batch = batches.find(b => b.index === batchIndex);
      if (!batch) {
        logger.error(`[ChunkedUpload] Batch ${batchIndex} not found in provided batches`);
        errors.push({ batchIndex, error: 'Batch data not found' });
        continue;
      }

      updateProgress({
        currentBatchIndex: batchIndex,
        status: 'uploading',
        estimatedRemainingBatches: pendingIndices.length - pendingIndices.indexOf(batchIndex)
      }, options.onProgress);

      const result = await uploadBatch(
        sessionId,
        batch,
        batchIndex,
        session.totalBatches,
        options
      );

      if (result.success) {
        newCompletedCount++;
        currentCompletedProducts += batch.products.length;
        updateProgress({
          completedBatches: newCompletedCount,
          completedProducts: currentCompletedProducts
        }, options.onProgress);
      } else {
        errors.push({ batchIndex, error: result.error || 'Unknown error' });
        updateProgress({
          failedBatches: session.failedBatches.length + errors.length
        }, options.onProgress);
      }

      // Release memory
      batch.products = [];
    }

    // Determine final status
    const finalStatus = isPausedRef.current
      ? 'paused'
      : errors.length === 0
        ? 'completed'
        : 'error';

    updateProgress({
      status: finalStatus,
      currentBatchIndex: null
    }, options.onProgress);

    // Clear session if completed successfully
    if (!isPausedRef.current && errors.length === 0) {
      const finalSession = await BatchSessionManager.getSession(sessionId);
      if (finalSession && finalSession.pendingBatchIndices.length === 0 && finalSession.failedBatches.length === 0) {
        await BatchSessionManager.clearSession(sessionId);
      }
    }

    return {
      success: errors.length === 0,
      sessionId,
      totalBatches: session.totalBatches,
      completedBatches: newCompletedCount,
      failedBatches: session.failedBatches.length + errors.length,
      errors
    };
  }, [uploadBatch, updateProgress]);

  /**
   * Retries failed batches from a session
   */
  const retryFailedBatches = useCallback(async (
    sessionId: string,
    batches: ProductBatch[],
    options: ChunkedUploadOptions = {}
  ): Promise<ChunkedUploadResult> => {
    logger.log(`[ChunkedUpload] Retrying failed batches for session ${sessionId}`);

    // Move failed batches back to pending
    await BatchSessionManager.markFailedBatchesForRetry(sessionId);

    // Resume upload (which will pick up the formerly failed batches)
    return resumeBatchUpload(sessionId, batches, options);
  }, [resumeBatchUpload]);

  /**
   * Pauses the current upload
   */
  const pauseUpload = useCallback(async () => {
    logger.log('[ChunkedUpload] Pause requested');
    isPausedRef.current = true;

    if (currentSessionRef.current) {
      await BatchSessionManager.pauseSession(currentSessionRef.current);
    }
  }, []);

  /**
   * Checks for a resumable session
   */
  const checkForResumableSession = useCallback(async (): Promise<BatchSession | null> => {
    return BatchSessionManager.getResumableSession();
  }, []);

  /**
   * Gets progress for a specific session
   */
  const getSessionProgress = useCallback(async (sessionId: string): Promise<BatchUploadProgress | null> => {
    return BatchSessionManager.getProgress(sessionId);
  }, []);

  /**
   * Clears a session (e.g., when user chooses to start fresh)
   */
  const clearSession = useCallback(async (sessionId: string): Promise<void> => {
    await BatchSessionManager.clearSession(sessionId);
    if (currentSessionRef.current === sessionId) {
      currentSessionRef.current = null;
    }
    updateProgress({
      sessionId: '',
      totalBatches: 0,
      completedBatches: 0,
      failedBatches: 0,
      currentBatchIndex: null,
      totalProducts: 0,
      completedProducts: 0,
      status: 'idle',
      estimatedRemainingBatches: 0
    });
  }, [updateProgress]);

  return {
    progress,
    startBatchUpload,
    resumeBatchUpload,
    retryFailedBatches,
    pauseUpload,
    checkForResumableSession,
    getSessionProgress,
    clearSession
  };
};
